import React, {
  forwardRef,
  memo,
  MouseEvent,
  useEffect,
  useState,
} from "react";
import moment, { Moment } from "moment";
import { AdapterMoment } from "@mui/x-date-pickers-pro/AdapterMoment";
import {
  DateCalendar,
  DateRangeCalendar,
  LocalizationProvider,
  PickersDayProps,
  TimePicker,
} from "@mui/x-date-pickers-pro";
import { ruRU } from "@mui/x-date-pickers/locales";
import { Box } from "@mui/material";
import {
  NutritionDay,
  CalendarHeader,
  TodayLabel,
  ActionButtons,
} from "./components";
import "./calendar.css";
import { CalendarProps, TCalendarValue } from "./calendar.types";
import { locale } from "./const";
import { betweenTwoDates } from "app/utils";

const CalendarComponent = forwardRef<HTMLDivElement, CalendarProps>(
  (
    {
      isShown = false,
      onSelected = () => {},
      isMultiChoice = false,
      isDisableToday,
      isDisableBeforeToday,
      isDisableAfterToday,
      disableBeforeDate,
      disableAfterDate,
      disabledDates,
      startDate,
      endDate,
      onClose,
      month: value,
      children,
      isNutrition,
      nutritionDates,
      onChangeTime,
      maxValue,
    },
    ref
  ) => {
    // Controlled range date state
    const [rangeDate, setRangeDate] = useState<TCalendarValue>([
      startDate ? moment(startDate) : undefined,
      endDate ? moment(endDate) : undefined,
    ]);

    const [date, setDate] = useState<Moment | null>(
      (value && moment(value)) as Moment
    );

    // Props for both calendars
    const calendarProps = {
      ref: ref,
      disableFuture: isDisableAfterToday,
      disableHighlightToday: true,
      disablePast: isDisableBeforeToday,
      showDaysOutsideCurrentMonth: true,
    };

    // Update locale
    moment.updateLocale("ru", locale);

    // Close calendar
    const missClickCalendar = (e: MouseEvent) => {
      e.stopPropagation();
      if (onClose !== undefined) {
        onClose();
      }
    };

    // Disable dates
    const shouldDisable = (day: Date | undefined | moment.Moment) => {
      if (!day) return false;
      if (isDisableToday && moment(day).isSame(moment(), "day")) return true;
      if (
        disableBeforeDate &&
        moment(day).isBefore(moment(disableBeforeDate))
      ) {
        return true;
      }
      if (disableAfterDate && moment(day).isAfter(moment(disableAfterDate))) {
        return true;
      }
      if (
        maxValue &&
        rangeDate[0]?.toDate() &&
        moment(day).isAfter(
          new Date(rangeDate[0].toDate()).getTime() +
            maxValue * 24 * 60 * 60 * 1000
        )
      ) {
        return true;
      }
      if (disabledDates && disabledDates.length > 0)
        for (let dates of disabledDates) {
          if (
            moment(day).isSameOrAfter(dates.from) &&
            moment(day).isSameOrBefore(dates.to)
          )
            return true;
        }
      return false;
    };

    // Nutrition day component
    const Nutrition = (props: PickersDayProps) => {
      return <NutritionDay {...props} nutritionDates={nutritionDates} />;
    };

    useEffect(() => {
      setRangeDate([
        startDate ? moment(startDate) : undefined,
        endDate ? moment(endDate) : undefined,
      ]);
    }, [startDate, endDate]);

    return (
      isShown && (
        <>
          <div
            className="miss-click-handler"
            onClick={(e) => missClickCalendar(e)}
          ></div>
          <div className="calendar-mui">
            <LocalizationProvider
              dateAdapter={AdapterMoment}
              adapterLocale="ru"
              localeText={
                ruRU.components.MuiLocalizationProvider.defaultProps.localeText
              }
            >
              {isMultiChoice ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "white",
                  }}
                >
                  <DateRangeCalendar
                    fixedWeekNumber={6}
                    referenceDate={value && moment(value)}
                    defaultValue={[rangeDate[0] || null, rangeDate[1] || null]}
                    value={[rangeDate[0] || null, rangeDate[1] || null]}
                    dayOfWeekFormatter={(day) => day.format("dd").toUpperCase()}
                    slots={{ calendarHeader: CalendarHeader }}
                    onChange={(value) => {
                      if (
                        value[0] &&
                        value[1] &&
                        betweenTwoDates(value[0].toDate(), value[1].toDate()) <
                          0
                      ) {
                        onSelected([value[1].toDate(), undefined]);
                        setRangeDate([value[1], undefined]);
                      } else {
                        onSelected(value.map((item) => item?.toDate()));
                        setRangeDate([
                          value[0] || undefined,
                          value[1] || undefined,
                        ]);
                      }
                    }}
                    shouldDisableDate={(day) => shouldDisable(day)}
                    calendars={1}
                    {...calendarProps}
                  />
                  <TodayLabel />
                  {!children && (
                    <ActionButtons
                      onSubmit={() => onClose?.()}
                      onReset={() => {
                        onSelected([undefined, undefined]);
                        setRangeDate([undefined, undefined]);
                      }}
                    />
                  )}
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                    margin: "auto",
                    backgroundColor: "white",
                  }}
                >
                  <DateCalendar
                    defaultValue={
                      (startDate || value) &&
                      (moment(startDate) || moment(value))
                    }
                    fixedWeekNumber={6}
                    value={date}
                    onChange={(value) => {
                      onSelected(value?.toDate());
                      setDate(value || null);
                    }}
                    shouldDisableDate={(day) => shouldDisable(day)}
                    slots={{
                      ...(isNutrition && { day: Nutrition }),
                      calendarHeader: CalendarHeader,
                    }}
                    {...calendarProps}
                  />
                  {onChangeTime && <TodayLabel />}
                  {onChangeTime && (
                    <TimePicker
                      value={date}
                      onChange={(time) => {
                        onChangeTime(
                          `${Number(time?.hour()) || 0}:${Number(time?.minute()) || 0}`
                        );
                        setDate(time);
                      }}
                      format="HH:mm"
                      sx={{ p: 2 }}
                    />
                  )}
                  {onChangeTime && (
                    <ActionButtons
                      onSubmit={() => onClose?.()}
                      onReset={() => {
                        onSelected(undefined);
                        setDate(null);
                      }}
                    />
                  )}
                </Box>
              )}
            </LocalizationProvider>
            {children}
          </div>
        </>
      )
    );
  }
);

export default memo(CalendarComponent);
